<template>
  <div class="office-layout">
    <!-- 顶部导航栏 -->
    <header class="office-header">
      <div class="header-left">
        <span class="header-title">办公系统</span>
      </div>
      <div class="header-right">
        <el-dropdown trigger="click" @command="handleCommand">
          <span class="user-info">
            <el-avatar :size="32" :src="userStore.avatar || ''">
              {{ userStore.nickname?.substring(0, 1) || 'U' }}
            </el-avatar>
            <span class="username">{{ userStore.nickname || '用户' }}</span>
            <el-icon><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">
                <el-icon><User /></el-icon>个人信息
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <el-icon><SwitchButton /></el-icon>退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </header>

    <!-- 左侧菜单 -->
    <aside class="office-sidebar">
      <el-menu
        :default-active="activeMenu"
        :router="true"
        :collapse="false"
        class="office-menu"
      >
        <el-menu-item index="/office">
          <el-icon><HomeFilled /></el-icon>
          <span>工作台</span>
        </el-menu-item>

        <!-- 客户管理 -->
        <el-sub-menu index="/office/customers">
          <template #title>
            <el-icon><User /></el-icon>
            <span>客户管理</span>
          </template>
          <el-menu-item index="/office/customers">客户列表</el-menu-item>
          <el-menu-item index="/office/customers/create">新增客户</el-menu-item>
          <el-menu-item index="/office/customer-departments">部门管理</el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="/office/warehouse">
          <template #title>
            <el-icon><Box /></el-icon>
            <span>仓库管理</span>
          </template>
          <el-menu-item index="/office/warehouse">仓库概览</el-menu-item>
          <el-menu-item index="/office/warehouse/item-list">物品管理</el-menu-item>
          <el-menu-item index="/office/warehouse/item-categories">物品类别管理</el-menu-item>
          <el-menu-item index="/office/warehouse/suppliers">供应商管理</el-menu-item>
          <el-menu-item index="/office/warehouse/stock-orders">出入库管理</el-menu-item>
          <el-menu-item index="/reports/inventory/records"><el-icon><PieChart /></el-icon><span>库存流水</span></el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="/office/shipping-orders">
          <template #title>
            <el-icon><Van /></el-icon>
            <span>发货订单</span>
          </template>
          <el-menu-item index="/office/shipping-orders">订单管理</el-menu-item>
          <el-menu-item index="/office/shipping-orders/create">新建订单</el-menu-item>
          <el-menu-item index="/office/shipping-orders/statistics">发货统计</el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="/office/contracts">
          <template #title>
            <el-icon><Document /></el-icon>
            <span>合同管理</span>
          </template>
          <el-menu-item index="/office/contracts">合同台账</el-menu-item>
          <el-menu-item index="/office/contracts/create">新建合同</el-menu-item>
          <el-menu-item index="/office/contracts/statistics">合同统计</el-menu-item>
        </el-sub-menu>

        <el-sub-menu index="/office/repair">
          <template #title>
            <el-icon><Tools /></el-icon>
            <span>维修管理</span>
          </template>
          <el-menu-item index="/office/repair/request">维修申请</el-menu-item>
          <el-menu-item index="/office/repair/report">维修报告</el-menu-item>
        </el-sub-menu>

        <!-- 采购管理 -->
        <el-sub-menu index="/office/purchase">
          <template #title>
            <el-icon><Tickets /></el-icon>
            <span>采购管理</span>
          </template>
          <el-menu-item index="/office/purchase-requests">采购申请单</el-menu-item>
          <el-menu-item index="/office/purchase-requests/create">新建申请</el-menu-item>
          <el-menu-item index="/office/warehouse/purchase-order-list">采购单管理</el-menu-item>
          <el-menu-item index="/office/purchase-orders/statistics">采购统计</el-menu-item>
        </el-sub-menu>

        <!-- 审批管理 -->
        <el-sub-menu index="/office/approval">
          <template #title>
            <el-icon><Clock /></el-icon>
            <span>审批管理</span>
          </template>
          <el-menu-item index="/office/approval/my-applications">
            <span>我的申请</span>
          </el-menu-item>
          <el-menu-item index="/office/approval/pending">
            <span>待审批</span>
          </el-menu-item>
          <el-menu-item index="/office/approval/history">
            <span>审批历史</span>
          </el-menu-item>
          <el-menu-item index="/office/approval/settings">
            <span>审批设置</span>
          </el-menu-item>
        </el-sub-menu>

        <!-- 企业微信管理 -->
        <el-sub-menu index="/wecom" v-if="canSeeWecom">
          <template #title>
            <el-icon><ChatDotRound /></el-icon>
            <span>企业微信</span>
          </template>
          <el-menu-item index="/wecom/department">部门管理</el-menu-item>
          <el-menu-item index="/wecom/user">用户管理</el-menu-item>
          <el-menu-item index="/wecom/approvals">审批管理</el-menu-item>
          <el-menu-item index="/wecom/message">消息管理</el-menu-item>
          <el-menu-item index="/wecom/robot">群机器人</el-menu-item>
          <el-menu-item index="/wecom/approvals-analytics">审批统计</el-menu-item>
        </el-sub-menu>

        <!-- 财务报表 -->
        <el-menu-item index="/office/reports/financial">
          <el-icon><TrendCharts /></el-icon>
          <span>财务报表</span>
        </el-menu-item>
      </el-menu>
    </aside>

    <!-- 主内容区 -->
    <main class="office-main">
      <PageHeader />
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- AI 助手按钮 -->
    <AIAssistantButton />
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { hasRole } from '@/utils/permission'
import AIAssistantButton from '@/components/AIAssistantButton.vue'
import PageHeader from '@/components/PageHeader.vue'
import {
  HomeFilled,
  Box,
  Document,
  Tools,
  User,
  ArrowDown,
  SwitchButton,
  ChatDotRound,
  PieChart,
  Van,
  Tickets,
  TrendCharts,
  CheckCircle,
  Desktop,
  ShoppingCart,
  Truck,
  Clock,
  History,
  Setting,
  Document as DocumentIcon,
  List
} from '@element-plus/icons-vue'

export default {
  name: 'OfficeLayout',
  components: {
    HomeFilled,
    Box,
    Document,
    Tools,
    User,
    ArrowDown,
    SwitchButton,
    AIAssistantButton,
    ChatDotRound,
    PageHeader,
    PieChart,
    Van,
    Tickets,
    TrendCharts,
    CheckCircle,
    Desktop,
    ShoppingCart,
    Truck,
    Clock,
    History,
    Setting,
    DocumentIcon,
    List
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()

    // 计算当前激活的菜单项
    const activeMenu = computed(() => {
      const { path } = route
      if (path.startsWith('/office/customers') || path.startsWith('/office/customer-departments')) {
        return path
      }
      if (path.startsWith('/office/warehouse')) {
        return path
      }
      if (path.startsWith('/office/shipping-orders')) {
        return path
      }
      if (path.startsWith('/office/contracts')) {
        return path
      }
      if (path.startsWith('/office/repair')) {
        return path
      }
      if (path.startsWith('/office/purchase-requests')) {
        return path
      }
      if (path.startsWith('/office/approval')) {
        return path
      }
      if (path.startsWith('/wecom')) {
        return path
      }
      if (path.startsWith('/reports/inventory/records')) {
        return path
      }
      if (path.startsWith('/office/reports/financial')) {
        return path
      }
      return path
    })

    // 处理用户下拉菜单命令
    const handleCommand = async (command) => {
      switch (command) {
        case 'profile':
          router.push('/profile')
          break
        case 'logout':
          try {
            await userStore.logout()
            router.push('/login')
          } catch (error) {
            console.error('登出失败:', error)
          }
          break
      }
    }

    // 企业微信菜单可见性（角色：admin、super_admin、manager）
    const canSeeWecom = computed(() => hasRole(['admin', 'super_admin', 'manager']))

    return {
      userStore,
      activeMenu,
      handleCommand,
      canSeeWecom
    }
  }
}
</script>

<style lang="scss" scoped>
.office-layout {
  min-height: 100vh;
  display: grid;
  grid-template-areas:
    "header header"
    "sidebar main";
  grid-template-columns: 240px 1fr;
  grid-template-rows: 60px 1fr;
  position: relative;
}

.office-header {
  grid-area: header;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  position: relative;

  .header-left {
    .el-breadcrumb {
      font-size: 14px;
    }
  }

  .header-right {
    .user-info {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.3s;

      &:hover {
        background-color: #f5f7fa;
      }

      .username {
        font-size: 14px;
        color: #606266;
      }
    }
  }
}

.office-sidebar {
  grid-area: sidebar;
  background: #fff;
  border-right: 1px solid #e6e6e6;
  overflow-y: auto;
  position: relative;
  z-index: 10;

  .office-menu {
    border-right: none;
  }
}

.office-main {
  grid-area: main;
  padding: 20px;
  background: #f5f7fa;
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

// 路由切换动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 全局 AI 助手按钮样式
:deep(.ai-assistant-button) {
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 3000;
}

@media screen and (max-width: 768px) {
  .office-layout {
    grid-template-areas:
      "header"
      "main";
    grid-template-columns: 1fr;
  }

  .office-sidebar {
    display: none;
  }
}
</style> 